import type { EmployeeSummary } from "@/hooks/useMakersBoardData";
import type { Assignment, AssignmentEmployee } from "@/hooks/useProjectBoardData";
import { fullRowChanges, recordAudit, type AuditChanges } from "@/lib/audit";
import { db } from "@/lib/supabase";
import type { Database } from "@/types/database.types";
import { withWeeklyHoursSummary } from "@/utils/auditWeeklyHours";
import { mustUnconfirm } from "@/utils/assignmentConfirmation";

type MarkSelfEdit = (table: string, id: string, ttlMs?: number) => void;

/** Audit context a role-structure write needs beyond the row itself. */
export interface AssignmentAuditContext {
  recordLabel?: string | null;
  officeId?: string | null;
  /** The project's start date, used to summarise weekly hours in the entry. */
  projectStartDate: string;
}

/**
 * Insert one role, with its INSERT audit entry.
 *
 * Split out of the Find People modal so role creation is a single write the modal
 * can sequence with everything else in a bulk save. Returns the error message
 * rather than throwing so the caller can stop before attaching employees to a row
 * that was never created.
 */
export async function createAssignment(
  insert: Database["public"]["Tables"]["project_assignments"]["Insert"],
  markSelfEdit: MarkSelfEdit,
  audit: AssignmentAuditContext,
): Promise<{ error: string | null }> {
  try {
    if (insert.id) markSelfEdit("project_assignments", insert.id);
    const { data, error } = await db.projectAssignments.create(insert);
    if (error) {
      console.error("Failed to create assignment:", error);
      return { error: error.message ?? "Failed to create role" };
    }

    recordAudit({
      table: "project_assignments",
      recordId: data.id,
      operation: "INSERT",
      changes: withWeeklyHoursSummary(fullRowChanges(data, "INSERT"), audit.projectStartDate),
      recordLabel: audit.recordLabel,
      officeId: audit.officeId,
    });
    return { error: null };
  } catch (err) {
    console.error("Error creating assignment:", err);
    return { error: err instanceof Error ? err.message : "Failed to create role" };
  }
}

/**
 * Delete one role, with its DELETE audit entry. The audit records the row as it
 * stood before the delete, since it won't be readable afterwards.
 */
export async function deleteAssignment(
  assignment: Assignment,
  roleName: string,
  markSelfEdit: MarkSelfEdit,
  audit: AssignmentAuditContext,
): Promise<{ error: string | null }> {
  try {
    markSelfEdit("project_assignments", assignment.id);
    const { error } = await db.projectAssignments.delete(assignment.id);
    if (error) {
      console.error("Failed to delete assignment:", error);
      return { error: error.message ?? "Failed to delete role" };
    }

    recordAudit({
      table: "project_assignments",
      recordId: assignment.id,
      operation: "DELETE",
      changes: withWeeklyHoursSummary(
        fullRowChanges(
          {
            id: assignment.id,
            project_id: assignment.project_id,
            required_role: roleName,
            weekly_hours_allocated: assignment.weekly_hours_allocated,
            is_confirmed: assignment.is_confirmed,
            notes: assignment.notes,
          },
          "DELETE",
        ),
        audit.projectStartDate,
      ),
      recordLabel: audit.recordLabel,
      officeId: audit.officeId,
    });
    return { error: null };
  } catch (err) {
    console.error("Error deleting assignment:", err);
    return { error: err instanceof Error ? err.message : "Failed to delete role" };
  }
}

export function employeeSummaryToAssignmentEmployee(employee: EmployeeSummary): AssignmentEmployee {
  return {
    id: employee.id,
    name: employee.name ?? null,
    nickname: employee.nickname ?? null,
    role: Array.isArray(employee.role) ? employee.role[0] ?? null : employee.role ?? null,
    weekly_capacity_hours: employee.weeklyCapacityHours ?? null,
  };
}

interface AssignmentEmployeesUpdate {
  cols: Database["public"]["Tables"]["project_assignments"]["Update"];
  changes: AuditChanges;
  changed: boolean;
}

export function buildAssignmentEmployeesUpdate(
  oldEmployeeIds: string[],
  newEmployeeIds: string[],
  wasConfirmed: boolean | null | undefined,
  nameById: Map<string, string> = new Map(),
): AssignmentEmployeesUpdate {
  const sortedOld = [...oldEmployeeIds].sort();
  const sortedNew = [...newEmployeeIds].sort();
  const changed = JSON.stringify(sortedOld) !== JSON.stringify(sortedNew);
  // Written for every change except a one-for-one swap, even if this client
  // thinks the role is already unlocked: its copy may be stale.
  const unconfirms = changed && mustUnconfirm(oldEmployeeIds.length, newEmployeeIds.length);
  // The audit entry only claims a lock was broken when we know there was one.
  const breaksConfirmation = unconfirms && Boolean(wasConfirmed);

  const cols: Database["public"]["Tables"]["project_assignments"]["Update"] = unconfirms
    ? { is_confirmed: false }
    : {};

  const toNames = (ids: string[]) => ids.map((id) => nameById.get(id) ?? id);
  const changes: AuditChanges = {};
  if (changed) {
    changes.employees = {
      old: toNames(oldEmployeeIds),
      new: toNames(newEmployeeIds),
    };
  }
  if (breaksConfirmation) {
    changes.is_confirmed = { old: true, new: false };
  }

  return { cols, changes, changed };
}

/** What the database holds for the assignment after a successful save. */
export interface SavedAssignmentEmployees {
  /** The row's is_confirmed as written, or null if the RPC didn't report it. */
  isConfirmed: boolean | null;
}

/**
 * Replace an assignment's employee set. Resolves with the saved state on
 * success, or null on failure (after onError has run).
 */
export async function saveAssignmentEmployees(
  assignment: Assignment,
  selectedEmployeeIds: Set<string>,
  markSelfEdit: (table: string, id: string, ttlMs?: number) => void,
  onSuccess: () => void | Promise<void> = () => {},
  onError: (errorMessage: string) => void | Promise<void> = () => {},
  audit: {
    recordLabel?: string | null;
    nameById?: Map<string, string>;
    officeId?: string | null;
  } = {},
): Promise<SavedAssignmentEmployees | null> {
  const oldEmployeeIds = assignment.employees.map((employee) => employee.id);
  const newEmployeeIds = Array.from(selectedEmployeeIds);
  const { cols, changes, changed } = buildAssignmentEmployeesUpdate(
    oldEmployeeIds,
    newEmployeeIds,
    assignment.is_confirmed,
    audit.nameById,
  );

  try {
    markSelfEdit("project_assignments", assignment.id);
    // Atomic delete-all + insert of the new employee set (plus the is_confirmed
    // reset when the change breaks a confirmation), so a failed relink can't
    // leave the slot with zero assignees or a stale lock.
    const { data, error } = await db.projectAssignments.updateWithEmployees(
      assignment.id,
      cols,
      newEmployeeIds,
      true,
    );

    if (error) {
      console.error("Failed to update employee assignments:", error);
      await onError(error.message ?? "Failed to update employee assignments");
      return null;
    }

    if (changed) {
      recordAudit({
        table: "project_assignments",
        recordId: assignment.id,
        operation: "UPDATE",
        changes,
        recordLabel: audit.recordLabel,
        officeId: audit.officeId,
      });
    }
    return { isConfirmed: persistedIsConfirmed(data) };
  } catch (err) {
    console.error("Error assigning employees:", err);
    await onError(err instanceof Error ? err.message : "Failed to assign employees");
    return null;
  } finally {
    await onSuccess();
  }
}

/** The RPC returns { new: <row> }; read the saved is_confirmed from it. */
function persistedIsConfirmed(data: unknown): boolean | null {
  const row = (data as { new?: { is_confirmed?: unknown } } | null)?.new;
  return typeof row?.is_confirmed === "boolean" ? row.is_confirmed : null;
}

export async function clearAssignmentEmployees(
  assignment: Assignment,
  markSelfEdit: (table: string, id: string, ttlMs?: number) => void,
  onSuccess: () => void | Promise<void> = () => {},
  onError: (errorMessage: string) => void | Promise<void> = () => {},
  audit: {
    recordLabel?: string | null;
    nameById?: Map<string, string>;
    officeId?: string | null;
  } = {},
) {
  if (assignment.employees.length === 0) {
    await onSuccess();
    return;
  }
  await saveAssignmentEmployees(
    assignment,
    new Set<string>(),
    markSelfEdit,
    onSuccess,
    onError,
    audit,
  );
}
